import { slicedToArray as _slicedToArray } from '../../_virtual/_rollupPluginBabelHelpers.js';
import { formatDate } from '@costar/react-datepicker';
import { useSafeThemeUI, Flex, Button } from '@costar/theme-ui';
import Autocomplete from '@costar/theme-ui-autocomplete';
import getYear from 'date-fns/getYear';
import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { TODAY } from '../../constants/index.esm.js';
import { generateMonthList, generateYearList, getFormattedMonth, getFormattedYear } from '../../utils/dates.esm.js';
import { getLocaleFormattedYear } from '../../utils/register-culture.esm.js';

// import { NumberOrRangeText } from '@costar/analytics-formatters'

var CustomHeader = function CustomHeader(props) {
  var maxDate = props.maxDate,
    minDate = props.minDate,
    _props$monthFormat = props.monthFormat,
    monthFormat = _props$monthFormat === void 0 ? 'MMM' : _props$monthFormat,
    changeMonth = props.changeMonth,
    changeYear = props.changeYear,
    increaseYear = props.increaseYear,
    increaseMonth = props.increaseMonth,
    decreaseYear = props.decreaseYear,
    decreaseMonth = props.decreaseMonth,
    showMonthSelect = props.showMonthSelect,
    prevMonthButtonDisabled = props.prevMonthButtonDisabled,
    nextMonthButtonDisabled = props.nextMonthButtonDisabled,
    date = props.date,
    _props$mode = props.mode,
    mode = _props$mode === void 0 ? 'day' : _props$mode,
    _props$locale = props.locale,
    locale = _props$locale === void 0 ? 'en-US' : _props$locale,
    _props$fontSize = props.fontSize,
    fontSize = _props$fontSize === void 0 ? 'sm' : _props$fontSize,
    fontFamily = props.fontFamily;
  var theme = useSafeThemeUI(props);
  var currentYear = date.getFullYear();
  var months = useMemo(function () {
    return generateMonthList(monthFormat, locale, currentYear, minDate, maxDate);
  }, [monthFormat, locale, currentYear, minDate, maxDate]);
  var years = useMemo(function () {
    return generateYearList(locale, date, minDate, maxDate);
  }, [date, maxDate, minDate, locale]);
  var _useState = useState(function () {
      return getFormattedMonth(monthFormat, locale, date, months);
    }),
    _useState2 = _slicedToArray(_useState, 2),
    month = _useState2[0],
    setMonth = _useState2[1];
  var _useState3 = useState(function () {
      return getFormattedYear(locale, date, years);
    }),
    _useState4 = _slicedToArray(_useState3, 2),
    year = _useState4[0],
    setYear = _useState4[1];
  useEffect(function () {
    if (formatDate(date, monthFormat, locale) !== month.label) setMonth(getFormattedMonth(monthFormat, locale, date, months));
    if (getLocaleFormattedYear(locale, getYear(date)) !== year.label) setYear(getFormattedYear(locale, date, years));
  }, [date, locale, monthFormat, months, years, month, year.label]);
  var optionsSelection = useMemo(function () {
    return {
      monthIndex: date.getMonth(),
      year: new Date(date).getFullYear()
    };
  }, [date]);
  var shouldDisable = useMemo(function () {
    var yearKeys = Object.keys(years);
    var firstYear = years[parseInt(yearKeys[0])].label;
    var lastYear = years[parseInt(yearKeys[yearKeys.length - 1])].label;
    return {
      IncreaseYearButton: mode === 'day' || mode === 'week' || mode === 'fourWeeks' ? nextMonthButtonDisabled : mode === 'month' ? optionsSelection.year >= lastYear : optionsSelection.year >= Number.parseInt(yearKeys[yearKeys.length - 1]) && optionsSelection.monthIndex >= TODAY.asDate.getMonth(),
      DecreaseYearButton: mode === 'day' || mode === 'week' || mode === 'fourWeeks' ? prevMonthButtonDisabled : mode === 'month' ? optionsSelection.year <= firstYear : optionsSelection.year <= Number.parseInt(yearKeys[0]) && optionsSelection.monthIndex == 0
    };
  }, [mode, optionsSelection.year, optionsSelection.monthIndex, years, prevMonthButtonDisabled, nextMonthButtonDisabled]);
  var handleMonthOnChange = useCallback(function (_event, selection) {
    if (!selection || !selection.sortOrder) return;
    if (typeof selection.sortOrder == 'string') {
      changeMonth(Number(selection.sortOrder) - 1);
    } else {
      changeMonth(selection.sortOrder - 1);
    }
    setMonth(selection);
  }, [changeMonth]);
  var handleYearOnChange = useCallback(function (_event, selection) {
    if (!selection || !selection.label) return;
    if (typeof selection.label === 'string') {
      changeYear(Number.parseInt(selection.label));
    }
    setYear(selection);
  }, [changeYear]);
  var handleRightButtonClick = useCallback(function () {
    mode === 'month' ? increaseYear() : increaseMonth();
  }, [mode, increaseYear, increaseMonth]);
  var handleLeftButtonClick = useCallback(function () {
    mode === 'month' ? decreaseYear() : decreaseMonth();
  }, [mode, decreaseYear, decreaseMonth]);
  var styleOverridesDecrease = useMemo(function () {
    return {
      visibility: shouldDisable.DecreaseYearButton ? 'hidden' : 'visible'
    };
  }, [shouldDisable.DecreaseYearButton]);
  var styleOverridesIncrease = useMemo(function () {
    return {
      visibility: shouldDisable.IncreaseYearButton ? 'hidden' : 'visible'
    };
  }, [shouldDisable.IncreaseYearButton]);
  var isOptionEqualToValue = useCallback(function (option, value) {
    return option.label === value.label;
  }, []);
  var getOptionDisabled = useCallback(function (option) {
    return !!option.disable;
  }, []);
  var autocompleteComponentProps = useMemo(function () {
    return {
      listbox: {
        fontSize: fontSize,
        fontFamily: fontFamily
      }
    };
  }, [fontSize, fontFamily]);
  return /*#__PURE__*/React.createElement(Flex, {
    relative: true,
    width: "100%",
    justifyContent: "space-evenly"
  }, /*#__PURE__*/React.createElement(Button, {
    icon: theme.direction === 'rtl' ? 'chevronRight' : 'chevronLeft',
    variant: "ghost",
    onClick: handleLeftButtonClick,
    disabled: prevMonthButtonDisabled || shouldDisable.DecreaseYearButton,
    color: "neutral.dark",
    style: styleOverridesDecrease,
    p: 0
  }), /*#__PURE__*/React.createElement(Flex, {
    flexWrap: "nowrap",
    justifyContent: "space-around",
    width: "70%",
    gap: "sm"
  }, showMonthSelect && /*#__PURE__*/React.createElement(Autocomplete, {
    value: month,
    fontSize: "sm",
    fullWidthPopover: true,
    selectOnFocus: true,
    clearOnBlur: true,
    handleHomeEndKeys: true,
    onChange: handleMonthOnChange,
    options: months,
    isOptionEqualToValue: isOptionEqualToValue,
    getOptionDisabled: getOptionDisabled,
    width: "100%",
    componentsProps: autocompleteComponentProps,
    disableClearable: true
  }), /*#__PURE__*/React.createElement(Autocomplete, {
    value: year,
    fontSize: "sm",
    fullWidthPopover: true,
    selectOnFocus: true,
    clearOnBlur: true,
    handleHomeEndKeys: true,
    onChange: handleYearOnChange,
    options: years,
    isOptionEqualToValue: isOptionEqualToValue,
    getOptionDisabled: getOptionDisabled,
    width: "100%",
    componentsProps: autocompleteComponentProps,
    disableClearable: true
  })), /*#__PURE__*/React.createElement(Button, {
    icon: theme.direction === 'rtl' ? 'chevronLeft' : 'chevronRight',
    variant: "ghost",
    onClick: handleRightButtonClick,
    disabled: nextMonthButtonDisabled || shouldDisable.IncreaseYearButton,
    color: "neutral.dark",
    style: styleOverridesIncrease,
    p: 0
  }));
};

export { CustomHeader };
//# sourceMappingURL=custom-header.esm.js.map
